<template>
    <div class="lti-tool-activity tw:flex tw:flex-col tw:gap-6">
        <e-field>
            <display-content :content="displayContent" />
        </e-field>

        <div>
            <e-loading v-model="loading" :is-full-page="false" />

            <div v-if="!loading && resourceLink">
                <e-button type="is-primary" icon-right="external-link-alt" @click="showLaunchDrawer = true">
                    Launch Assignment
                </e-button>

                <lti-resource-link-drawer
                    :active="showLaunchDrawer"
                    :lti-resource-link-id="resourceLink.id"
                    :title="drawerTitle"
                    refresh-on-close
                    @update:active="showLaunchDrawer = $event"
                />
            </div>
        </div>

        <activity-completion-action-row v-if="!loading && resourceLink" />

        <template v-if="!readOnly && canViewSubmissions">
            <e-message type="is-info" class="lti-tool-activity__submissions-notice mb-4">
                After submitting your assignment in {{ toolName }}, it may take a few moments for your submission to
                appear here.
            </e-message>
            <submissions :namespace="namespace" @fetch-submissions="toggleSubmissionLoading">
                <template #content>
                    <e-button type="is-primary" @click="showLaunchDrawer = true">
                        {{ viewInToolLabel }}
                    </e-button>
                </template>
            </submissions>
        </template>
    </div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import ActivityCompletionActionRow from '@/components/activity-types/course-activity-types/ActivityCompletionActionRow.vue';
import Submissions from '@/components/activity-types/course-activity-types/activities/Submissions.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import LtiResourceLinkDrawer from '@/components/lti/LtiResourceLinkDrawer.vue';
import { useActivity } from '@/composables/useActivity.js';
import LtiResourceLink from '@/models/LtiResourceLink.js';
import axios from '@/plugins/axios.js';

export default {
    name: 'LtiTool',

    components: { ActivityCompletionActionRow, LtiResourceLinkDrawer, DisplayContent, Submissions },

    props: {
        namespace: {
            type: String,
            default: null,
        },

        uuid: {
            type: String,
            default: null,
        },

        readOnly: {
            type: Boolean,
            default: false,
        },

        provideSubmissions: {
            type: Array,
            required: false,
            default: () => null,
        },
    },

    setup(props, ctx) {
        const instance = getCurrentInstance();
        const api = useActivity(props, ctx, { getPayload: () => instance.proxy.payload });

        return { ...api };
    },

    data() {
        return { loading: true, resourceLink: null, showLaunchDrawer: false };
    },

    computed: {
        id() {
            return this.$store.getters[`${this.namespace}/get`](this.uuid)?.id ?? null;
        },

        resource() {
            return this.namespace === 'Activities' ? 'activities' : 'activity-templates';
        },

        displayContent() {
            return this.activityContent?.content ?? '';
        },

        drawerTitle() {
            const toolName = this.resourceLink?.deployment?.tool?.name ?? null;
            return toolName ? `${toolName} Launch` : 'LTI Tool Launch';
        },

        toolName() {
            return this.resourceLink?.deployment?.tool?.name ?? 'Tool';
        },

        viewInToolLabel() {
            return `View in ${this.toolName}`;
        },

        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.courseId,
                activity_id: this.activityId,
                activity_type: this.get('activity_type'),
                activity_submission: [],
            };
        },
    },

    watch: {
        id: {
            immediate: true,
            handler() {
                if (!this.id) {
                    return;
                }
                this.loadResourceLink();
            },
        },
    },

    methods: {
        async loadResourceLink() {
            this.loading = true;

            try {
                const { data } = await axios.get(
                    `/api/${this.resource}/${this.id}?include=contentable.ltiResourceLink`
                );
                const rawResourceLink = data?.data?.activity_content?.lti_resource_link ?? null;

                if (rawResourceLink) {
                    await LtiResourceLink.insert({ data: rawResourceLink });
                    this.resourceLink = LtiResourceLink.query()
                        .with(['deployment', 'deployment.tool'])
                        .find(rawResourceLink.id);
                } else {
                    this.resourceLink = null;
                }
            } catch (error) {
                this.resourceLink = null;
                this.$edverumToast.open({
                    message: 'Unable to load this LTI tool. Please refresh and try again.',
                    type: 'is-danger',
                });
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
@import 'resources/scss/toolbox.scss';

.lti-tool-activity {
    > * {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
    }

    :deep(.activity-completion-action-row) {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
    }

    :deep(.activity-submissions-section) {
        margin-bottom: 0 !important;
        margin-top: 0 !important;
    }
}

// Desktop/tablet (>= 769px) must render exactly as before the mobile spacing work
// (868kj17g6 AC-6, ADR-051 decision 5). The previous markup stacked Bulma `.columns`
// (1.5rem rhythm, $column-gap padding), so only the spots where that rhythm differs from
// the flat 1.5rem gap are corrected here; mobile is unchanged.
@media screen and (min-width: 769px) {
    // Last `.column` padding plus the row's own `tw:mt-4` (it out-collapses the
    // `.columns` bottom margin): $column-gap + 1rem above the action row.
    .lti-tool-activity > .activity-completion-action-row {
        margin-top: calc(#{$column-gap} + 1rem - 1.5rem) !important;
    }

    // The submissions `.columns` top margin cancelled its `.column` padding, so the notice
    // started right under the action row.
    .lti-tool-activity > .activity-completion-action-row + .lti-tool-activity__submissions-notice {
        margin-top: -1.5rem !important;
    }

    // The notice kept its own `mb-4` (1rem) above the submissions list.
    .lti-tool-activity > .lti-tool-activity__submissions-notice {
        margin-bottom: calc(1rem - 1.5rem) !important;
    }
}
</style>
